import React from "react";
import { useDraggable } from "@dnd-kit/core";
import { useNavigate } from "react-router-dom";
import { Clock, Car, User } from "lucide-react";
import { StatusBadge } from "@/components/Badges";
import { computeTotalWorkTimeAw } from "@/components/WorkItemRow";
import { useTimeFormat } from "@/hooks/useTimeFormat";

const priorityRing = {
  low: "border-l-zinc-500",
  normal: "border-l-blue-500",
  high: "border-l-amber-500",
  urgent: "border-l-red-500",
};

/**
 * Compact / comfortable contract card for the planning board.
 * The ENTIRE card is draggable (no grip handle). A plain click still opens
 * the contract — dnd-kit's `activationConstraint` (distance ≥ 5px) prevents
 * accidental drags on click. `size="lg"` gives extra breathing room for the
 * new single-employee week view where each column is wider.
 */
export default function PlanningContractCard({ contract, customer, vehicle, size = "md" }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: contract.id });
  const nav = useNavigate();
  const tf = useTimeFormat();
  const totalAw = computeTotalWorkTimeAw(contract.work_items || []);
  const headline = vehicle
    ? `${vehicle.brand} ${vehicle.model}`
    : customer
      ? customer.name
      : contract.title || contract.number;
  const plate = vehicle?.license_plate || "";
  const custName = customer?.name || "";

  const openContract = (e) => {
    if (isDragging) return;
    e.stopPropagation();
    nav(`/contracts/${contract.id}`);
  };

  const sizeCls = size === "lg"
    ? "p-3 text-sm gap-1.5"
    : "p-2 text-xs gap-1";
  const headlineCls = size === "lg"
    ? "text-sm md:text-[15px]"
    : "text-[13px]";
  const metaCls = size === "lg" ? "text-xs" : "text-[11px]";

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={openContract}
      className={`group relative rounded-lg bg-[#171B24] border border-[#2E3241] border-l-[3px] ${priorityRing[contract.priority] || priorityRing.normal} hover:border-blue-500/50 hover:bg-[#1E222C] transition-colors cursor-grab active:cursor-grabbing select-none flex flex-col ${sizeCls} ${isDragging ? "dragging shadow-xl ring-1 ring-blue-500/50" : ""}`}
      data-testid={`plan-card-${contract.number}`}
    >
      <div className={`font-display font-semibold text-zinc-100 group-hover:text-blue-300 truncate leading-tight ${headlineCls}`}>
        {headline}
      </div>
      {(plate || custName) && (
        <div className={`flex items-center gap-3 text-zinc-400 ${metaCls} truncate`}>
          {plate && (
            <span className="flex items-center gap-1 truncate">
              <Car className="h-3 w-3 shrink-0" />
              <span className="font-mono-stat truncate">{plate}</span>
            </span>
          )}
          {custName && custName !== headline && (
            <span className="flex items-center gap-1 truncate text-zinc-500">
              <User className="h-3 w-3 shrink-0" />
              <span className="truncate">{custName}</span>
            </span>
          )}
        </div>
      )}
      {contract.title && (
        <div className={`text-zinc-500 truncate ${metaCls}`}>{contract.title}</div>
      )}
      <div className={`flex items-center justify-between mt-auto pt-1 ${metaCls}`}>
        <div className="flex items-center gap-2.5 text-zinc-500">
          <span className="font-mono-stat text-zinc-200" data-testid={`plan-card-worktime-${contract.number}`}>
            {tf.format(totalAw)}
          </span>
          {contract.planned_time && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" /> {contract.planned_time}
            </span>
          )}
          <span className="font-mono-stat text-zinc-500">{contract.number}</span>
        </div>
        <StatusBadge status={contract.status} />
      </div>
    </div>
  );
}
